import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for role management and the permission matrix.
 *
 * `RolesController` had only `POST` before Phase 4; the listing, update, delete
 * and both permission endpoints are new, lifted from what the two page models
 * did against `RoleManager` directly.
 */

const BASE = '/api/roles';

export interface RoleListItem {
  id: string;
  name: string;
  description: string;
  displayOrder: number;
  isSystemRole: boolean;
  userCount: number;
  permissionCount: number;
  createdDate: string;
  createdBy: string;
}

export interface RolesResponse {
  roles: RoleListItem[];
  availablePermissions: string[];
  statistics: { total: number; system: number; custom: number };
}

export interface RolePermissions {
  roleId: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface RolePermissionsMatrix {
  roles: RolePermissions[];
  allPermissions: string[];
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  displayOrder: number;
}

export type UpdateRoleRequest = Omit<CreateRoleRequest, 'name'> & { id: string };

/**
 * Groups permissions by the area they govern — Users, Roles, AuditLogs, Menus.
 *
 * The second dotted segment, not the first. `RolePermissions.cshtml.cs`'s
 * `GetPermissionsByCategory` took `parts[0]`, but every permission value begins
 * with the literal `Permissions.`, so every one of them landed in a single
 * category called "Permissions" and the per-category headings the page renders
 * collapsed into one. Taking the second segment is what those headings were for.
 */
export function permissionsByCategory(permissions: string[]): [string, string[]][] {
  const grouped = new Map<string, string[]>();

  for (const permission of permissions) {
    const parts = permission.split('.');
    const category = parts.length > 2 ? parts[1] : (parts[1] ?? 'Other');
    const bucket = grouped.get(category);
    if (bucket) bucket.push(permission);
    else grouped.set(category, [permission]);
  }

  return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
}

/** The trailing segment — what the checkbox label shows next to the full value. */
export function permissionAction(permission: string): string {
  const parts = permission.split('.');
  return parts[parts.length - 1] ?? permission;
}

export const roleKeys = {
  all: ['admin', 'roles'] as const,
  list: ['admin', 'roles', 'list'] as const,
  permissions: ['admin', 'roles', 'permissions'] as const,
};

export function useRoles() {
  return useQuery<RolesResponse>({
    queryKey: roleKeys.list,
    queryFn: () => api.get<RolesResponse>(BASE),
  });
}

export function useRolePermissions() {
  return useQuery<RolePermissionsMatrix>({
    queryKey: roleKeys.permissions,
    queryFn: () => api.get<RolePermissionsMatrix>(`${BASE}/permissions`),
  });
}

/**
 * Broad on purpose: changing a role's permissions moves the count shown on the
 * roles grid, and creating one adds a tab to the matrix.
 */
function useInvalidateRoles() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: roleKeys.all });
}

export function useCreateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (request: CreateRoleRequest) => api.post(BASE, request),
    onSuccess: invalidate,
  });
}

export function useUpdateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateRoleRequest) => api.put(`${BASE}/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: invalidate,
  });
}

/** Replaces one role's permissions with exactly the set given. */
export function useSetRolePermissions() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ roleName, permissions }: { roleName: string; permissions: string[] }) =>
      api.put(`${BASE}/${encodeURIComponent(roleName)}/permissions`, { permissions }),
    onSuccess: invalidate,
  });
}
